import { create } from 'zustand';
import api from '../lib/api';

const TRANSIENT_STATUSES = new Set([502, 503, 504]);

async function postActivityWithRetry(url: string, data: Record<string, unknown>, attempts = 3) {
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await api.post(url, data);
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      const transient = status === undefined || TRANSIENT_STATUSES.has(status);
      lastErr = err;
      if (!transient || i === attempts - 1) throw err;
      await new Promise((r) => setTimeout(r, 200 * (i + 1)));
    }
  }
  throw lastErr;
}
import type {
  Activity,
  ActivityCreate,
  PaginationMeta,
  Temperature,
  ScheduleActivityCreate,
  CompleteActivityPayload,
} from '../types';

interface ActivityState {
  // List state (per person)
  activities: Activity[];
  pagination: PaginationMeta | null;
  currentPersonId: string | null;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;
  canEdit: boolean;

  // Create/Delete state
  isSaving: boolean;
  saveError: string | null;

  // Upcoming activities state
  upcomingActivities: Activity[];
  isLoadingUpcoming: boolean;

  // Actions - List
  fetchActivities: (personId: string, page?: number) => Promise<void>;
  setPage: (page: number) => void;
  clearActivities: () => void;

  // Actions - CRUD
  createActivity: (
    personId: string,
    data: ActivityCreate
  ) => Promise<{ activity: Activity; updatedTemperature: Temperature | null; updatedLastActivityAt: string | null }>;
  deleteActivity: (personId: string, activityId: string) => Promise<void>;
  prependActivity: (activity: Activity) => void;

  // Actions - Scheduled activities
  scheduleActivity: (personId: string, data: ScheduleActivityCreate) => Promise<Activity>;
  completeActivity: (personId: string, activityId: string, data: CompleteActivityPayload) => Promise<{ activity: Activity; person: unknown }>;
  cancelActivity: (personId: string, activityId: string, reason?: string) => Promise<void>;
  fetchUpcoming: (personId: string) => Promise<void>;
  clearUpcoming: () => void;

  // Global activities state (for Activities page)
  globalActivities: Activity[];
  globalPagination: PaginationMeta | null;
  globalFilters: {
    search?: string;
    type?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    personId?: string;
    userId?: string;
  };
  globalPage: number;
  isLoadingGlobal: boolean;

  // Actions - Global
  fetchGlobalActivities: () => Promise<void>;
  setGlobalFilters: (filters: Record<string, string | undefined>) => void;
  setGlobalPage: (page: number) => void;

  // Actions - Utility
  clearError: () => void;
  clearSaveError: () => void;

  // Detail panel (CRM-29)
  openActivityDetailId: string | null;
  detailActivity: Activity | null;
  isLoadingDetail: boolean;
  detailError: string | null;
  openActivityDetail: (id: string) => Promise<void>;
  closeActivityDetail: () => void;

  // Org activity feed (CRM-26)
  orgActivities: Activity[];
  orgActivitiesPagination: PaginationMeta | null;
  orgActivitiesLoading: boolean;
  orgActivityUserId: string | null;
  fetchOrgActivities: (orgId: string, page?: number) => Promise<void>;
  setOrgActivityRepFilter: (orgId: string, userId: string | null) => void;
  clearOrgActivities: () => void;

  // Bumped on any activity mutation so feeds can refetch (CRM-26)
  activityChangeNonce: number;
}

export const useActivityStore = create<ActivityState>((set, get) => ({
  // Initial state - List
  activities: [],
  pagination: null,
  currentPersonId: null,
  currentPage: 1,
  pageSize: 25,
  isLoading: false,
  error: null,
  canEdit: false,

  // Initial state - Create/Delete
  isSaving: false,
  saveError: null,

  // Initial state - Upcoming
  upcomingActivities: [],
  isLoadingUpcoming: false,

  // Initial state - Global
  globalActivities: [],
  globalPagination: null,
  globalFilters: {},
  globalPage: 1,
  isLoadingGlobal: false,

  // Initial state - Detail panel (CRM-29)
  openActivityDetailId: null,
  detailActivity: null,
  isLoadingDetail: false,
  detailError: null,

  // Initial state - Org feed (CRM-26)
  orgActivities: [],
  orgActivitiesPagination: null,
  orgActivitiesLoading: false,
  orgActivityUserId: null,

  // Change nonce (CRM-26)
  activityChangeNonce: 0,

  // ==================== LIST ACTIONS ====================

  /**
   * Fetch activities for a person with pagination
   */
  fetchActivities: async (personId: string, page?: number) => {
    const { currentPage, pageSize } = get();
    const pageToFetch = page ?? currentPage;

    try {
      set({ isLoading: true, error: null, currentPersonId: personId });

      const params = new URLSearchParams();
      params.append('page', pageToFetch.toString());
      params.append('limit', pageSize.toString());

      const response = await api.get(
        `/api/people/${personId}/activities?${params.toString()}`
      );

      set({
        activities: response.data.activities,
        pagination: response.data.pagination,
        canEdit: response.data.canEdit,
        currentPage: pageToFetch,
        isLoading: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch activities:', error);
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to load activities',
      });
    }
  },

  /**
   * Set current page and refetch
   */
  setPage: (page: number) => {
    const { currentPersonId } = get();
    if (currentPersonId) {
      set({ currentPage: page });
      get().fetchActivities(currentPersonId, page);
    }
  },

  /**
   * Clear activities (when navigating away from person)
   */
  clearActivities: () => {
    set({
      activities: [],
      pagination: null,
      currentPersonId: null,
      currentPage: 1,
      error: null,
      canEdit: false,
    });
  },

  // ==================== CRUD ACTIONS ====================

  /**
   * Create a new activity
   * Returns the new activity and updated person fields
   */
  createActivity: async (personId: string, data: ActivityCreate) => {
    try {
      set({ isSaving: true, saveError: null });

      const payload = { ...data, idempotencyKey: data.idempotencyKey ?? crypto.randomUUID() };
      const response = await postActivityWithRetry(`/api/people/${personId}/activities`, payload as Record<string, unknown>);

      const { activity, person } = response.data;

      // Add new activity to the beginning of the list (most recent first)
      set((state) => ({
        activities: [activity, ...state.activities],
        pagination: state.pagination
          ? { ...state.pagination, totalCount: state.pagination.totalCount + 1 }
          : null,
        isSaving: false,
        activityChangeNonce: state.activityChangeNonce + 1,
      }));

      return {
        activity,
        updatedTemperature: person?.temperature,
        updatedLastActivityAt: person?.lastActivityAt,
      };
    } catch (error: any) {
      console.error('Failed to create activity:', error);
      const message = error.response?.data?.message || 'Failed to log activity';
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  /**
   * Delete an activity
   */
  deleteActivity: async (personId: string, activityId: string) => {
    try {
      set({ isSaving: true, saveError: null });

      await api.delete(`/api/people/${personId}/activities/${activityId}`);

      // Remove activity from list
      set((state) => ({
        activities: state.activities.filter((a) => a.id !== activityId),
        pagination: state.pagination
          ? { ...state.pagination, totalCount: state.pagination.totalCount - 1 }
          : null,
        isSaving: false,
        activityChangeNonce: state.activityChangeNonce + 1,
      }));
    } catch (error: any) {
      console.error('Failed to delete activity:', error);
      const message = error.response?.data?.message || 'Failed to delete activity';
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  /**
   * Optimistically prepend an already-created activity to the current list
   * (e.g. after a successful in-app email send) without a network round-trip.
   * Mirrors the createActivity insert.
   */
  prependActivity: (activity: Activity) => {
    set((state) => ({
      activities: [activity, ...state.activities],
      pagination: state.pagination
        ? { ...state.pagination, totalCount: state.pagination.totalCount + 1 }
        : null,
      activityChangeNonce: state.activityChangeNonce + 1,
    }));
  },

  // ==================== SCHEDULED ACTIVITY ACTIONS ====================

  /**
   * Schedule a new activity (POST with status: 'PLANNED')
   * Returns the created activity
   */
  scheduleActivity: async (personId: string, data: ScheduleActivityCreate) => {
    try {
      set({ isSaving: true, saveError: null });

      const payload = { ...data, idempotencyKey: data.idempotencyKey ?? crypto.randomUUID() };
      const response = await postActivityWithRetry(`/api/people/${personId}/activities`, payload as Record<string, unknown>);
      const { activity } = response.data;

      set({ isSaving: false });

      // Refetch upcoming after scheduling
      await get().fetchUpcoming(personId);

      return activity;
    } catch (error: any) {
      console.error('Failed to schedule activity:', error);
      const message = error.response?.data?.message || 'Failed to schedule activity';
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  /**
   * Complete a planned activity
   * Returns { activity, person }
   */
  completeActivity: async (personId: string, activityId: string, data: CompleteActivityPayload) => {
    try {
      set({ isSaving: true, saveError: null });

      const response = await api.patch(
        `/api/people/${personId}/activities/${activityId}/complete`,
        data
      );

      set({ isSaving: false, activityChangeNonce: get().activityChangeNonce + 1 });

      // Refetch both upcoming and activities list after completing
      await Promise.all([
        get().fetchUpcoming(personId),
        get().fetchActivities(personId),
      ]);

      return response.data;
    } catch (error: any) {
      console.error('Failed to complete activity:', error);
      const message = error.response?.data?.message || 'Failed to complete activity';
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  /**
   * Cancel a planned activity
   */
  cancelActivity: async (personId: string, activityId: string, reason?: string) => {
    try {
      set({ isSaving: true, saveError: null });

      await api.patch(`/api/people/${personId}/activities/${activityId}/cancel`, {
        reason,
      });

      set({ isSaving: false, activityChangeNonce: get().activityChangeNonce + 1 });

      // Refetch upcoming after cancelling
      await get().fetchUpcoming(personId);
    } catch (error: any) {
      console.error('Failed to cancel activity:', error);
      const message = error.response?.data?.message || 'Failed to cancel activity';
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  /**
   * Fetch upcoming (PLANNED) activities for a person
   */
  fetchUpcoming: async (personId: string) => {
    try {
      set({ isLoadingUpcoming: true });

      const response = await api.get(
        `/api/people/${personId}/activities?status=PLANNED`
      );

      set({
        upcomingActivities: response.data.activities,
        isLoadingUpcoming: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch upcoming activities:', error);
      set({ isLoadingUpcoming: false });
    }
  },

  /**
   * Clear upcoming activities list
   */
  clearUpcoming: () => {
    set({ upcomingActivities: [] });
  },

  // ==================== GLOBAL ACTIVITY ACTIONS ====================

  /**
   * Fetch activities across all people (for Activities page)
   */
  fetchGlobalActivities: async () => {
    try {
      set({ isLoadingGlobal: true });
      const { globalFilters, globalPage } = get();
      const params = new URLSearchParams();
      params.append('page', globalPage.toString());
      params.append('limit', '25');
      if (globalFilters.search) params.append('search', globalFilters.search);
      if (globalFilters.type) params.append('type', globalFilters.type);
      if (globalFilters.status) params.append('status', globalFilters.status);
      if (globalFilters.dateFrom) params.append('dateFrom', globalFilters.dateFrom);
      if (globalFilters.dateTo) params.append('dateTo', globalFilters.dateTo);
      if (globalFilters.personId) params.append('personId', globalFilters.personId);
      if (globalFilters.userId) params.append('userId', globalFilters.userId);

      const response = await api.get(`/api/activities?${params.toString()}`);
      set({
        globalActivities: response.data.activities,
        globalPagination: response.data.pagination,
        isLoadingGlobal: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch global activities:', error);
      set({ isLoadingGlobal: false });
    }
  },

  /**
   * Set filters for global activities and refetch (resets to page 1)
   */
  setGlobalFilters: (filters) => {
    set({ globalFilters: { ...get().globalFilters, ...filters }, globalPage: 1 });
    get().fetchGlobalActivities();
  },

  /**
   * Set page for global activities and refetch
   */
  setGlobalPage: (page) => {
    set({ globalPage: page });
    get().fetchGlobalActivities();
  },

  // ==================== DETAIL PANEL ACTIONS (CRM-29) ====================

  /**
   * Open the activity detail panel and fetch the activity
   */
  openActivityDetail: async (id: string) => {
    set({ openActivityDetailId: id, isLoadingDetail: true, detailError: null, detailActivity: null });
    try {
      const response = await api.get(`/api/activities/${id}`);
      set({ detailActivity: response.data.activity, isLoadingDetail: false });
    } catch (error: any) {
      set({ isLoadingDetail: false, detailError: error.response?.data?.message || 'Failed to load activity' });
    }
  },

  /**
   * Close the activity detail panel and clear detail state
   */
  closeActivityDetail: () => {
    set({ openActivityDetailId: null, detailActivity: null, detailError: null, isLoadingDetail: false });
  },

  // ==================== ORG ACTIVITY FEED ACTIONS (CRM-26) ====================

  /**
   * Fetch activities for an organization; appends when page > 1 (infinite scroll)
   */
  fetchOrgActivities: async (orgId: string, page = 1) => {
    try {
      set({ orgActivitiesLoading: true });
      const { orgActivityUserId } = get();
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '25');
      if (orgActivityUserId) params.append('userId', orgActivityUserId);
      const response = await api.get(`/api/organizations/${orgId}/activities?${params.toString()}`);
      set((state) => ({
        orgActivities: page > 1 ? [...state.orgActivities, ...response.data.activities] : response.data.activities,
        orgActivitiesPagination: response.data.pagination,
        orgActivitiesLoading: false,
      }));
    } catch (error: any) {
      console.error('Failed to fetch org activities:', error);
      set({ orgActivitiesLoading: false });
    }
  },

  /**
   * Set rep filter for org activity feed and refetch from page 1
   */
  setOrgActivityRepFilter: (orgId: string, userId: string | null) => {
    set({ orgActivityUserId: userId });
    get().fetchOrgActivities(orgId, 1);
  },

  /**
   * Clear org activity feed state (when navigating away)
   */
  clearOrgActivities: () => {
    set({ orgActivities: [], orgActivitiesPagination: null, orgActivityUserId: null });
  },

  // ==================== UTILITY ACTIONS ====================

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Clear save error state
   */
  clearSaveError: () => {
    set({ saveError: null });
  },
}));
